import { useEffect, useState } from "react";

export type Gender = "women" | "men" | "non-binary";

export type HeightValue = "short" | "average_short" | "average_tall" | "tall" | "average";

export type SilhouetteProps = {
  gender: Gender | null;
  height: HeightValue | null;
  bodyShape: string | null;
  bodySize: string | null;
  skinTone: string | null;
  hair: string | null;
  isReveal?: boolean;
  className?: string;
};

const SKIN_COLOURS: Record<string, string> = {
  light: "#FDDBB4",
  light_medium: "#F5C18A",
  medium: "#D4956A",
  medium_deep: "#B07346",
  deep: "#7B4F2E",
  very_deep: "#4A2910",
};

const HAIR_COLOURS: Record<string, string> = {
  short_dark: "#1a1a1a",
  short_light: "#D4A855",
  long_dark: "#1a1a1a",
  long_light: "#D4A855",
  natural_afro: "#1a1a1a",
  medium_wavy: "#6B3F1A",
  locs_braids: "#1a1a1a",
  bald_shaved: "transparent",
  medium_length: "#6B3F1A",
  curly: "#1a1a1a",
};

// Body shape: returns a torso+legs path keyed by shape name.
// Coordinates are within a 200x440 viewBox; head is separately at top.
function bodyPathFor(shape: string | null, gender: Gender | null): string {
  // Defaults — neutral hourglass-ish
  // We define a left-half outline that's mirrored via path commands.
  const presets: Record<string, string> = {
    // Women
    hourglass:
      "M75 110 Q60 150 65 200 Q40 240 60 300 Q55 360 70 420 L130 420 Q145 360 140 300 Q160 240 135 200 Q140 150 125 110 Z",
    pear:
      "M82 110 Q72 150 78 200 Q40 250 55 310 Q50 370 65 420 L135 420 Q150 370 145 310 Q160 250 122 200 Q128 150 118 110 Z",
    athletic:
      "M70 110 Q66 160 72 210 Q60 260 68 310 Q66 370 78 420 L122 420 Q134 370 132 310 Q140 260 128 210 Q134 160 130 110 Z",
    curvy:
      "M70 110 Q55 155 65 205 Q35 250 55 310 Q50 370 65 420 L135 420 Q150 370 145 310 Q165 250 135 205 Q145 155 130 110 Z",
    petite:
      "M82 115 Q76 160 80 205 Q66 255 76 305 Q72 365 84 420 L116 420 Q128 365 124 305 Q134 255 120 205 Q124 160 118 115 Z",
    // Men
    slim_lean:
      "M80 110 Q74 160 78 210 Q72 260 78 310 Q76 370 84 420 L116 420 Q124 370 122 310 Q128 260 122 210 Q126 160 120 110 Z",
    regular_average:
      "M72 110 Q66 160 72 210 Q66 260 72 310 Q70 370 80 420 L120 420 Q130 370 128 310 Q134 260 128 210 Q134 160 128 110 Z",
    athletic_broad:
      "M58 110 Q56 160 70 210 Q62 260 70 310 Q70 370 80 420 L120 420 Q130 370 130 310 Q138 260 130 210 Q144 160 142 110 Z",
    stocky:
      "M60 110 Q54 160 64 215 Q56 265 66 315 Q64 370 78 420 L122 420 Q136 370 134 315 Q144 265 136 215 Q146 160 140 110 Z",
    plus_size:
      "M55 115 Q45 165 60 220 Q40 270 60 320 Q58 375 74 420 L126 420 Q142 375 140 320 Q160 270 140 220 Q155 165 145 115 Z",
    // Non-binary neutral variants
    curvy_fuller:
      "M68 110 Q58 155 68 205 Q44 250 60 310 Q56 370 70 420 L130 420 Q144 370 140 310 Q156 250 132 205 Q142 155 132 110 Z",
  };
  if (shape && presets[shape]) return presets[shape];
  // Fallback by gender
  if (gender === "men") return presets.regular_average;
  if (gender === "women") return presets.athletic;
  return presets.regular_average;
}

function hairPathFor(hair: string | null, _gender: Gender | null): string | null {
  if (!hair || hair === "bald_shaved") return null;
  const presets: Record<string, string> = {
    short_dark: "M76 38 Q100 18 124 38 Q126 56 122 64 L78 64 Q74 56 76 38 Z",
    short_light: "M76 38 Q100 18 124 38 Q126 56 122 64 L78 64 Q74 56 76 38 Z",
    long_dark:
      "M72 40 Q100 14 128 40 Q134 80 130 140 L120 145 Q118 90 100 90 Q82 90 80 145 L70 140 Q66 80 72 40 Z",
    long_light:
      "M72 40 Q100 14 128 40 Q134 80 130 140 L120 145 Q118 90 100 90 Q82 90 80 145 L70 140 Q66 80 72 40 Z",
    natural_afro:
      "M60 50 Q60 10 100 8 Q140 10 140 50 Q145 76 130 78 L70 78 Q55 76 60 50 Z",
    medium_wavy:
      "M72 38 Q100 16 128 38 Q134 70 128 100 L118 100 Q116 82 100 80 Q84 82 82 100 L72 100 Q66 70 72 38 Z",
    locs_braids:
      "M74 40 Q100 18 126 40 Q132 90 128 160 L120 160 L116 60 L100 60 L84 60 L80 160 L72 160 Q68 90 74 40 Z",
    medium_length:
      "M74 38 Q100 16 126 38 Q132 60 128 82 L72 82 Q68 60 74 38 Z",
    curly:
      "M62 46 Q62 14 100 12 Q138 14 138 46 Q142 70 130 74 L70 74 Q58 70 62 46 Z",
  };
  return presets[hair] ?? presets.short_dark;
}

function sizeScale(bodySize: string | null): number {
  switch (bodySize) {
    case "slim":
      return 0.95;
    case "fuller":
      return 1.08;
    case "plus_size":
      return 1.15;
    default:
      return 1;
  }
}

function heightScale(h: SilhouetteProps["height"]): number {
  if (h === "short") return 0.88;
  if (h === "average_short") return 0.96;
  if (h === "average_tall") return 1.04;
  if (h === "tall") return 1.12;
  if (h === "average") return 1;
  return 1;
}

export function SilhouetteModel({
  gender,
  height,
  bodyShape,
  bodySize,
  skinTone,
  hair,
  isReveal = false,
  className,
}: SilhouetteProps) {
  const [revealStarted, setRevealStarted] = useState(false);
  useEffect(() => {
    if (!isReveal) return;
    const t = setTimeout(() => setRevealStarted(true), 300);
    return () => clearTimeout(t);
  }, [isReveal]);

  const skin = skinTone ? SKIN_COLOURS[skinTone] ?? "#D4956A" : "#D4956A";
  const hairFill = hair ? HAIR_COLOURS[hair] ?? "#1a1a1a" : "#1a1a1a";
  const body = bodyPathFor(bodyShape, gender);
  const hairPath = hairPathFor(hair, gender);
  const bScale = sizeScale(bodySize);
  const hScale = heightScale(height);

  const clipPath = isReveal
    ? revealStarted
      ? "inset(0% 0 0 0)"
      : "inset(100% 0 0 0)"
    : undefined;

  return (
    <svg
      viewBox="0 0 200 460"
      className={className}
      style={{
        width: "100%",
        height: "100%",
        transform: `scaleY(${hScale})`,
        transformOrigin: "center bottom",
        transition: "transform 0.5s ease",
        clipPath,
        WebkitClipPath: clipPath,
        // 1.8s wipe when reveal starts
        ...(isReveal
          ? {
              transitionProperty: "clip-path, -webkit-clip-path, transform",
              transitionDuration: "1.8s, 1.8s, 0.5s",
              transitionTimingFunction: "ease-out, ease-out, ease",
            }
          : {}),
      }}
      aria-hidden
    >
      {/* layer-skin (head + body fills) — sits below outline */}
      <g
        id="layer-skin"
        style={{
          opacity: skinTone ? 0.95 : 0,
          transition: "opacity 0.4s ease",
        }}
      >
        <circle cx="100" cy="70" r="34" fill={skin} />
        <g
          transform={`translate(100, 110) scale(${bScale}, 1) translate(-100, -110)`}
          style={{ transition: "transform 0.4s ease" }}
        >
          <path d={body} fill={skin} />
        </g>
      </g>

      {/* layer-body — body shape outline emphasis */}
      <g
        id="layer-body"
        style={{
          opacity: bodyShape ? 1 : 0,
          transition: "opacity 0.4s ease, transform 0.4s ease",
        }}
      >
        <g
          transform={`translate(100, 110) scale(${bScale}, 1) translate(-100, -110)`}
          style={{ transition: "transform 0.4s ease" }}
        >
          <path
            d={body}
            fill="none"
            stroke="var(--l2f-ink, #2a2a2a)"
            strokeWidth={1.2}
            strokeLinejoin="round"
          />
        </g>
      </g>

      {/* layer-hair */}
      <g
        id="layer-hair"
        style={{
          opacity: hair && hair !== "bald_shaved" ? 1 : 0,
          transition: "opacity 0.4s ease",
        }}
      >
        {hairPath && <path d={hairPath} fill={hairFill} />}
      </g>

      {/* layer-outline — always visible */}
      <g id="layer-outline" style={{ opacity: 0.5 }}>
        <circle
          cx="100"
          cy="70"
          r="34"
          fill="none"
          stroke="var(--color-text-secondary, #6b6b6b)"
          strokeWidth={2}
        />
        <g
          transform={`translate(100, 110) scale(${bScale}, 1) translate(-100, -110)`}
          style={{ transition: "transform 0.4s ease" }}
        >
          <path
            d={body}
            fill="none"
            stroke="var(--color-text-secondary, #6b6b6b)"
            strokeWidth={2}
            strokeLinejoin="round"
          />
        </g>
      </g>
    </svg>
  );
}

export default SilhouetteModel;
